/**
 * @fileoverview Main Application Component - Root component and routing configuration
 * 
 * This is the primary entry point for the Harry Potter Quiz App, providing:
 * - React Router setup for client-side navigation
 * - Route protection for authenticated and admin users
 * - Global layout structure with header and main content areas
 * - User authentication state management utilities
 * 
 * App Structure:
 * - Public routes: Login (/) 
 * - Protected student routes: Home, Sorting Hat
 * - Protected admin routes: School Management, Leaderboard
 * - Authentication guards: RequireAuth, RequireAdmin
 * - User session utilities: getCurrentUser for accessing stored user data
 * 
 * @author Harry Potter Quiz App
 * @version 1.0.0
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './pages/Login.jsx';
import Home from './pages/Home.jsx';
import SortingHat from './pages/SortingHat.jsx';
import AdminSchool from './pages/AdminSchool.jsx';
import AdminLeaderboard from './pages/AdminLeaderboard.jsx';
import './App.css';

/**
 * Main Application Component
 * 
 * Provides the root structure for the Harry Potter Quiz App including routing,
 * layout, and navigation between different sections of the application.
 * 
 * @component
 * @returns {JSX.Element} The complete application with routing and layout
 */
function App() {
  return (
    <Router>
      <div style={styles.appContainer}>
        {/* Application header with Harry Potter branding */}
        <header style={styles.appHeader}>
          <img 
            src="/images/harry_potter.png" 
            alt="Harry Potter Quiz App - Hogwarts School of Witchcraft and Wizardry" 
            style={styles.headerLogo}
          />
        </header>

        {/* Main content area with route-based components */}
        <main style={styles.mainContent}>
          <Routes>
            {/* Public authentication route */}
            <Route path="/" element={<Login />} />
            
            {/* Protected student routes */}
            <Route path="/home" element={<RequireAuth><Home /></RequireAuth>} />
            <Route path="/sorting-hat" element={<RequireAuth><SortingHat /></RequireAuth>} />
            
            {/* Protected administrator routes */}
            <Route path="/admin/school" element={<RequireAdmin><AdminSchool /></RequireAdmin>} />
            <Route path="/admin/leaderboard" element={<RequireAdmin><AdminLeaderboard /></RequireAdmin>} />
            
            {/* Fallback route - redirects unmatched paths to login */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
}

/**
 * Authentication Guard Component
 * 
 * Protects routes that require any authenticated user (student or admin).
 * Redirects unauthenticated users to the login page.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {JSX.Element} Protected children or redirect to login
 */
function RequireAuth({ children }) {
  const currentUser = getCurrentUser();
  if (!currentUser) return <Navigate to="/" replace />;
  return children;
}

/**
 * Admin Authorization Guard Component
 * 
 * Protects routes that require administrator privileges.
 * Redirects non-admin users or unauthenticated users to the login page.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if admin
 * @returns {JSX.Element} Protected children or redirect to login
 */
function RequireAdmin({ children }) {
  const currentUser = getCurrentUser();
  if (!currentUser || !currentUser.isAdmin) return <Navigate to="/" replace />;
  return children;
}

/**
 * User Session Utility Function
 * 
 * Retrieves and parses the current user data from localStorage.
 * This utility is used throughout the app to access user authentication
 * and authorization information.
 * 
 * @function getCurrentUser
 * @returns {Object|null} Parsed user object or null if not authenticated
 */
export function getCurrentUser() {
  try {
    const userDataJson = localStorage.getItem('hp_user');
    return userDataJson ? JSON.parse(userDataJson) : null;
  } catch (error) {
    console.error('Failed to parse user data from localStorage:', error);
    return null;
  }
}

/**
 * Consolidated style definitions for the main App component
 */
const styles = {
  appContainer: {
    minHeight: '100vh',
    backgroundColor: '#0b0f1a',
    color: '#fff',
  },
  
  appHeader: {
    padding: '16px 0',
    textAlign: 'center',
  },
  
  headerLogo: {
    height: 100,
    position: 'relative',
    zIndex: 2,
  },
  
  mainContent: {
    maxWidth: 960,
    margin: '0 auto',
    padding: 24,
  },
};

export default App;
